import { useEffect, useRef, useState } from "react";
import { Check, Pencil, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

// Sprint 7 T7.4.a — EditableField inline.
// Cf design/atomic-crm/details.jsx (composant EditableField) + détails
// visuels CSS (.edit-row / .edit-pen / .form-input).
//
// Pattern : ligne cliquable → mode édition avec input/select/textarea
// + boutons Annuler/Enregistrer. Optimistic update via onSave (le
// caller gère le dataProvider.update et l'erreur).

export type EditableFieldOption = { value: string; label: string };

interface EditableFieldProps {
  label: string;
  value: string | null | undefined;
  /** Affichage si différent de `value` (ex: label mappé d'un select). */
  displayValue?: React.ReactNode;
  /** Icône optionnelle à gauche de la valeur. */
  icon?: React.ReactNode;
  /** Élément optionnel à droite de la valeur (ex: lien mailto). */
  suffix?: React.ReactNode;
  /** "text" | "email" | "url" | "select" | "textarea". */
  type?: "text" | "email" | "url" | "select" | "textarea";
  /** Options du select si type="select". */
  options?: EditableFieldOption[];
  /** Si vrai, la ligne n'est pas cliquable. */
  readonly?: boolean;
  /** Callback de sauvegarde. Erreurs et notifs sont à la charge du caller. */
  onSave?: (next: string) => void | Promise<void>;
}

export const EditableField = ({
  label,
  value,
  displayValue,
  icon,
  suffix,
  type = "text",
  options,
  readonly = false,
  onSave,
}: EditableFieldProps) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState<string>(value ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setVal(value ?? "");
  }, [value]);

  useEffect(() => {
    if (!editing) return;
    // Focus à l'ouverture
    if (type === "select") {
      selectRef.current?.focus();
    } else if (type === "textarea") {
      textareaRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [editing, type]);

  const handleSave = async () => {
    if (val === (value ?? "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave?.(val);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setVal(value ?? "");
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    } else if (e.key === "Enter" && type !== "textarea") {
      e.preventDefault();
      void handleSave();
    } else if (
      e.key === "Enter" &&
      type === "textarea" &&
      (e.metaKey || e.ctrlKey)
    ) {
      e.preventDefault();
      void handleSave();
    }
  };

  if (editing && !readonly) {
    return (
      <div className="grid grid-cols-[180px_1fr] gap-3 items-start py-1.5 text-[13px] group">
        <span className="text-muted-foreground pt-1.5">{label}</span>
        <div className="flex items-center gap-1.5">
          {type === "select" && options ? (
            <select
              ref={selectRef}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-[13px] outline-none focus:ring-2 focus:ring-ring/40"
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : type === "textarea" ? (
            <Textarea
              ref={textareaRef}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              rows={3}
              className="flex-1 text-[13px]"
            />
          ) : (
            <Input
              ref={inputRef}
              type={type}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              className="flex-1 h-8 text-[13px]"
            />
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={handleCancel}
            disabled={saving}
            aria-label="Annuler"
            title="Annuler"
          >
            <X className="size-3.5" />
          </Button>
          <Button
            type="button"
            size="icon"
            className="size-7 shrink-0"
            onClick={handleSave}
            disabled={saving}
            aria-label="Enregistrer"
            title="Enregistrer"
          >
            <Check className="size-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-[180px_1fr] gap-3 items-baseline py-1.5 text-[13px] group rounded -mx-1.5 px-1.5",
        !readonly && "cursor-pointer hover:bg-muted/40",
      )}
      onClick={() => !readonly && setEditing(true)}
      role={!readonly ? "button" : undefined}
      tabIndex={!readonly ? 0 : undefined}
      onKeyDown={(e) => {
        if (!readonly && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          setEditing(true);
        }
      }}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground flex items-center gap-1.5 min-w-0">
        {icon && (
          <span className="shrink-0 text-muted-foreground [&>svg]:size-3.5">
            {icon}
          </span>
        )}
        <span className="truncate">
          {displayValue ?? value ?? (
            <em className="text-muted-foreground">—</em>
          )}
        </span>
        {suffix}
        {!readonly && (
          <Pencil className="size-3 text-muted-foreground/0 group-hover:text-muted-foreground/70 transition-colors ml-1 shrink-0" />
        )}
      </span>
    </div>
  );
};
